import React, { useEffect, useRef, useState } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../app/redux/hooks";
import { getProductDescriptionWithCategoryAsync } from "../../../../../../app/slices/productDiscriptionSlice/ProductDescriptionSlice";
import {
  Button,
  CircularProgress,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { ProductValues, SkuValues } from "../../productlistingTypes";
import {
  addOrUpdateCartAsync,
  fetchCartListAsync,
  removeItemFromCartAsync,
} from "../../../../../../app/slices/cartSlice/CartSlice";
import CustomButton from "../../../../../components/custom/button";
import { showSnackbar } from "../../../../../../app/slices/common/customSnackBar/customSnackbarSlice";
import { useNavigate } from "react-router-dom";
import { getPeopleBaughtListAsync } from "../../../../../../app/slices/productList/productListingSlice";
import { getDecryptedToken } from "../../../../../../app/auth/authHandler";

export const QuickAddModal = ({ modalType }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [productData, setProductData] = useState<ProductValues | null>(null);
  const [quantities, setQuantities] = useState<{ [skuId: number]: number }>({});
  const [selectedSku, setSelectedSku] = useState<SkuValues | null>(null);
  const [locationMessage, setLocationMessage] = useState<string | null>(null);
  const [action, setAction] = useState("Add");
  const [loading, setLoading] = useState(true);
  const [isEnable, setIsEnable] = useState(true);
  const isLargeScreen = useMediaQuery("(min-width:560px)");
  const [isQuantityLoading, setIsQuantityLoading] = useState(false);

  const { productDescriptionId } =
    useAppSelector((state) => state.productDescriptionState) || "";
  const { addTocart } = useAppSelector((state) => state.CartState);
  const cartData = addTocart?.data;
  const hasFetched = useRef(false); // ✅ stays same across renders
  useEffect(() => {
    if (modalType === "") {
      setProductData(null);
      setLoading(true);
    }
  }, [modalType, loading]);

  useEffect(() => {
    let product_id;

    if (productDescriptionId) {
      product_id = productDescriptionId;
    } else {
      product_id = localStorage.getItem("product_id");
    }
    if (!product_id || hasFetched.current) return; // ✅ prevents multiple calls
    hasFetched.current = true;
    const aId = localStorage.getItem("aId");
    const accessToken = getDecryptedToken();
    const anonymous_id: any = accessToken ? null : aId ? aId : null;
    if (product_id) {
      dispatch(
        getProductDescriptionWithCategoryAsync({ product_id, anonymous_id }),
      ).then((response: any) => {
        const res = response?.payload?.data;
        setProductData(res?.data);
        setLoading(false);
        setSelectedSku(
          res?.data?.skus.find((product: any) => product?.sku_status !== ""),
        );
      });
    }
    const quantities =
      cartData?.reduce((acc: any, item: any) => {
        acc[item.sku_id] = item.quantity;
        return acc;
      }, {}) || {};
    setQuantities(quantities);
  }, [productDescriptionId]);

  useEffect(() => {
    const quantities =
      cartData?.reduce((acc: any, item: any) => {
        acc[item.sku_id] = item.quantity;
        return acc;
      }, {}) || {};
    setQuantities(quantities);
  }, [cartData]);

  useEffect(() => {
    const selection = cartData.some(
      (p: any) => p.sku_id === selectedSku?.sku_id,
    );
    if (selection) {
      setAction("Update");
    } else {
      setAction("Add");
    }
  }, [selectedSku, cartData]);

  useEffect(() => {
    if (selectedSku?.same_day_delivery === false) {
      setLocationMessage("Same day delivery will not be available");
    } else {
      setLocationMessage(null);
    }
  }, [selectedSku]);

  const handleQuantityChange = async (
    skuId: number | undefined,
    action: "increase" | "decrease",
  ) => {
    if (skuId === undefined) return;

    const currentQuantity = quantities[skuId] || 0;
    const maxQuantity = selectedSku?.sku_bulk_qty_limit || Infinity;

    const newQuantity =
      action === "increase"
        ? Math.min(currentQuantity + 1, maxQuantity)
        : Math.max(currentQuantity - 1, 0);

    const updatedQuantities = {
      ...quantities,
      [skuId]: newQuantity,
    };

    setIsQuantityLoading(true); // Set loader before async work

    try {
      const cartItem = cartData.find((item: any) => item.sku_id === skuId);
      const cart_item_id = cartItem?.cart_item_id;

      if (newQuantity === 0 && cart_item_id) {
        const res: any = await dispatch(removeItemFromCartAsync(cart_item_id));
        if (res?.payload?.status === 200) {
          const aId = localStorage.getItem("aId");
          const accessToken = getDecryptedToken();
          const anonymous_id = accessToken ? null : aId || null;
          await dispatch(fetchCartListAsync(anonymous_id));
        }
      } else if (newQuantity !== currentQuantity) {
        const payload: any = {
          skus: Object.entries(updatedQuantities).map(([sku, qty]) => ({
            sku: Number(sku),
            quantity: qty,
          })),
        };

        const aId = localStorage.getItem("aId");
        const accessToken = getDecryptedToken();
        if (aId && !accessToken) {
          payload.anonymous_id = aId;
        }

        const response: any = await dispatch(addOrUpdateCartAsync(payload));

        if (response?.payload?.data?.status === 1) {
          setQuantities(updatedQuantities);
        } else {
          dispatch(
            showSnackbar({
              message:
                response?.payload?.data?.message || "Please try again later.",
              severity: "error",
              autoHide: 3000,
            }),
          );
        }

        // Only update message from API if it exists, otherwise keep the existing message
        if (response?.payload?.data?.location_message) {
          setLocationMessage(response?.payload?.data?.location_message);
        } else if (selectedSku?.same_day_delivery === false) {
          setLocationMessage("Same day delivery will not be available");
        }
      }
    } catch (error) {
      console.error("Cart update failed:", error);
    } finally {
      setIsQuantityLoading(false); // Stop loader after dispatch is complete
    }
  };

  // const handleQuantityChange = (
  //   skuId: number | undefined,
  //   action: "increase" | "decrease"
  // ) => {
  //   if (skuId === undefined) return;

  //   setQuantities((prevQuantities) => {
  //     const currentQuantity = prevQuantities[skuId] || 0;

  //     return {
  //       ...prevQuantities,
  //       [skuId]:
  //         action === "increase"
  //           ? Math.min(
  //               currentQuantity + 1,
  //               selectedSku?.sku_bulk_qty_limit || Infinity
  //             )
  //           : Math.max(currentQuantity - 1, 0),
  //     };
  //   });
  // };

  const calculateTotalAmount = (): number => {
    if (!productData) return 0;
    return productData?.skus?.reduce((total, sku) => {
      const quantity = quantities[sku.sku_id] || 0;
      const price = parseFloat(sku.offer_price_with_tax) || 0;
      return total + price * quantity;
    }, 0);
  };
  const selectSku = (sku: any) => {
    setSelectedSku(sku);
  };
  let v: any = quantities[selectedSku?.sku_id];

  // const addtocartButtonCall = () => {
  //   const payload: any = {
  //     skus: Object.entries(quantities)?.map(([sku, quantity]) => ({
  //       sku: Number(sku),
  //       quantity,
  //     })),
  //   };

  //   const aId = localStorage.getItem("aId");
  //   const accessToken = localStorage.getItem("accessToken");
  //   if (aId && accessToken == null) {
  //     payload.anonymous_id = aId;
  //   }
  //   dispatch(addOrUpdateCartAsync(payload)).then((response: any) => {
  //     if (response?.payload?.status === 200) {
  //       setIsEnable(false);
  //       dispatch(
  //         showSnackbar({
  //           message: "Cart updated succesfuly.",
  //           severity: "success",
  //           autoHide: 2000,
  //         })
  //       );
  //     } else {
  //       dispatch(
  //         showSnackbar({
  //           message: "Please try again later.",
  //           severity: "error",
  //           autoHide: 3000,
  //         })
  //       );
  //     }
  //   });
  // };

  const addtocartButtonCall = () => {
    const mappedSkus = Object.entries(quantities)?.map(([sku, quantity]) => ({
      sku: Number(sku),
      quantity,
    }));
    const payload: any = {
      skus: mappedSkus.filter((item) => {
        if (item.quantity === 0) {
          const cartItem = cartData.find(
            (cartItem: any) => cartItem.sku_id === selectedSku?.sku_id,
          );
          const cart_item_id = cartItem?.cart_item_id;
          dispatch(removeItemFromCartAsync(cart_item_id)).then(
            (response: any) => {
              if (response?.payload?.status === 200) {
                const aId = localStorage.getItem("aId");
                const accessToken = getDecryptedToken();
                const anonymous_id = accessToken ? null : aId ? aId : null;
                dispatch(fetchCartListAsync(anonymous_id));
              }
            },
          );
          return false;
        }
        return true;
      }),
    };

    if (payload.skus.length > 0) {
      const aId = localStorage.getItem("aId");
      const accessToken = getDecryptedToken();
      let anonymous_id: any;
      if (aId && !accessToken) {
        anonymous_id = aId;
      }
      payload.anonymous_id = anonymous_id;
      dispatch(addOrUpdateCartAsync(payload)).then((response: any) => {
        if (response?.payload?.status === 200) {
          // dispatch(getPeopleBaughtListAsync({ anonymous_id, product_id: "" }));
          setIsEnable(false);
          dispatch(
            showSnackbar({
              message: "Cart updated successfully.",
              severity: "success",
              autoHide: 2000,
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              message: "Please try again later.",
              severity: "error",
              autoHide: 3000,
            }),
          );
        }
      });
    }
  };

  const swiperRef = useRef(null);

  const handleBulletClick = (swiper: any) => {
    const activeIndex = swiper.realIndex;
    selectSku(productData?.skus[activeIndex]);
  };

  const isDisabled =
    selectedSku?.sku_id &&
    quantities[selectedSku.sku_id] >= selectedSku.sku_bulk_qty_limit;

  const percentageDisplay = (offer: any) => {
    if (!offer) {
      return null;
    }
    const percentage = parseFloat(offer.replace("%", ""));

    const formattedPercentage =
      percentage % 1 === 0
        ? `${Math.round(percentage)}%`
        : `${percentage.toFixed(2)}%`;

    return formattedPercentage;
  };
  return (
    <div id="quick_add">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ position: "relative" }}>
          {/* Fade overlay */}
          {isQuantityLoading && (
            <div
              className="fade-overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 9999,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontFamily: "Albert Sans, sans-serif",
              }}
            >
              <CircularProgress />
              <div
                style={{ marginTop: "12px", fontSize: "16px", color: "#333" }}
              >
                Updating to Cart...
              </div>
            </div>
          )}

          <div
            className="wrap"
            style={{
              fontFamily: "Albert Sans, sans-serif",
              opacity: isQuantityLoading ? 0.5 : 1,
              pointerEvents: isQuantityLoading ? "none" : "auto",
              transition: "opacity 0.3s ease-in-out",
            }}
          >
            {loading ? (
              <CircularProgress
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            ) : (
              <>
                <div className="modal-content">
                  <div
                    className="wrap"
                    style={{
                      fontFamily: "Albert Sans, sans-serif",
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    ) : (
                      <div>
                        <div className="tf-product-info-item">
                          <div className="image">
                            <img
                              src={`${process.env.REACT_APP_API_URL}${productData?.images[0]}`}
                              alt=""
                            />
                          </div>
                          <div className="content">
                            <div
                              className="variant-picker-item"
                              style={{ display: "flex" }}
                            >
                              <a>{productData?.product_name}</a> &nbsp; &nbsp;
                              <div
                                className="variant-picker-values"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  id="values-veg"
                                  type="radio"
                                  name="color1"
                                  checked
                                  readOnly
                                />
                                <label
                                  className={`hover-tooltip  ${
                                    productData?.product_type === "Veg"
                                      ? "veg"
                                      : "nonveg"
                                  }`}
                                  htmlFor="values-veg"
                                  data-value="Veg"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <span
                                    className={`btn-checkbox    ${
                                      productData?.product_type === "Veg"
                                        ? "bg-color-veg bg-veg"
                                        : "bg-color-nonveg "
                                    }`}
                                  ></span>
                                  <span className="tooltip">
                                    {productData?.product_type}
                                  </span>
                                </label>{" "}
                              </div>
                            </div>

                            <div className="tf-product-info-price">
                              <p
                                style={{
                                  color: "#94969F",
                                  fontSize: "14px",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  marginBottom: "0",
                                }}
                              >
                                {productData?.item_description}{" "}
                                <a
                                  onClick={() =>
                                    navigate(
                                      `/products/${productData?.product_id}`,
                                    )
                                  }
                                  style={{
                                    color: "#ef7d1f",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    textDecoration: "none",
                                  }}
                                  className="more-details-link"
                                >
                                  More Details
                                </a>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div
                          className="modal-shopping-cart"
                          style={{ overflow: "visible" }}
                        >
                          <div
                            className="tf-minicart-recommendations m-0"
                            style={{ overflow: "visible" }}
                          >
                            <div className="tf-minicart-recommendations-heading">
                              <div className="sw-dots cart-slide-pagination2 mt-0 small style-2"></div>
                            </div>

                            <div
                              className="hover-sw-nav"
                              style={{
                                overflow: "visible",
                                position: "relative",
                                width: "100%",
                              }}
                            >
                              <Swiper
                                modules={[Pagination, Navigation]}
                                onSwiper={(swiper: any) =>
                                  (swiperRef.current = swiper)
                                }
                                onSlideChange={handleBulletClick}
                                spaceBetween={10}
                                slidesPerView={1}
                                pagination={{
                                  el: ".cart-slide-pagination2",
                                  clickable: true,
                                }}
                                navigation={{
                                  nextEl: ".nav-next-slider",
                                  prevEl: ".nav-prev-slider",
                                }}
                                className="tf-cart-slide1"
                              >
                                {productData?.skus?.map((slide, index) => (
                                  <SwiperSlide key={index}>
                                    <div
                                      className="tf-minicart-recommendations-item"
                                      style={{
                                        overflowY: "auto",
                                        overflowX: isLargeScreen
                                          ? "auto"
                                          : "hidden", // Allow horizontal scroll only on large screens
                                        whiteSpace: isLargeScreen
                                          ? "nowrap"
                                          : "normal", // Prevent wrapping on large screens
                                        display: "flex",
                                        flexDirection: isLargeScreen
                                          ? "row"
                                          : "column", // Arrange items side-by-side or stacked
                                        gap: isLargeScreen ? "0px" : "10px", // Add spacing between the two divs
                                      }}
                                    >
                                      <div className="tf-minicart-recommendations-item-infos flex-grow-1">
                                        <a className="title" href="#">
                                          {slide?.sku_name?.length > 24 ? (
                                            <>
                                              {slide.sku_name.slice(0, 24)}
                                              <br />
                                              <span className="continuation">
                                                -{slide.sku_name.slice(24)}
                                              </span>
                                            </>
                                          ) : (
                                            slide.sku_name
                                          )}{" "}
                                          <br />
                                          {selectedSku?.sku_status === "Visible"
                                            ? `${
                                                selectedSku?.sku_quantity +
                                                selectedSku?.sku_unit
                                              } `
                                            : ""}
                                          {selectedSku?.sku_status !== "Visible"
                                            ? `(${
                                                selectedSku?.sku_quantity +
                                                selectedSku?.sku_unit +
                                                "-" +
                                                selectedSku?.sku_status
                                              } )`
                                            : ""}
                                        </a>
                                        <div className="tf-product-info-price">
                                          <div className="price-on-sale fs-16">
                                            ₹{slide.offer_price_with_tax}
                                          </div>

                                          {selectedSku?.offer &&
                                            selectedSku.offer !== "0%" && (
                                              <>
                                                <div className="compare-at-price">
                                                  ₹
                                                  {
                                                    selectedSku?.sku_mrp_with_tax
                                                  }
                                                </div>
                                                <div className="badges-on-sale">
                                                  <span>
                                                    {" "}
                                                    {percentageDisplay(
                                                      selectedSku?.offer,
                                                    )}
                                                  </span>{" "}
                                                  OFF
                                                </div>
                                              </>
                                            )}
                                        </div>
                                      </div>

                                      <div
                                        className="tf-product-info-buy-button"
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "end",
                                          justifyContent: "end",
                                          textAlign: "center",
                                          paddingBottom: "10px",
                                          width: "100%",
                                          marginLeft: isLargeScreen
                                            ? ""
                                            : "0px",
                                          // paddingRight: "10px",
                                        }}
                                      >
                                        {/* {isQuantityLoading ? (
                                        <CircularProgress
                                          style={{
                                            position: "absolute",
                                            top: "30%",
                                            left: "80%",
                                            width: "30px",
                                            height: "30px",
                                            transform: "translate(-50%, -50%)",
                                          }}
                                        />
                                      ) : (
                                        <> */}
                                        <div
                                          className="d-flex justify-content-end flex-grow-1"
                                          style={{ marginBottom: "5px" }}
                                        >
                                          <Button
                                            style={{
                                              background: "transparent",
                                              fontSize: "18px",
                                              color: "#000",
                                              cursor: "default",
                                            }}
                                            size="medium"
                                            disabled={
                                              (quantities[
                                                selectedSku?.sku_id
                                              ] === 0 ||
                                                quantities[
                                                  selectedSku?.sku_id
                                                ] === undefined) &&
                                              !cartData.some(
                                                (item: { sku_id: any }) =>
                                                  item.sku_id ===
                                                  selectedSku?.sku_id,
                                              )
                                            }
                                            onClick={addtocartButtonCall}
                                            // Check if your Button component supports loader
                                            // loader={addTocart?.loader}
                                          >
                                            {`₹\u00A0${calculateTotalAmount().toFixed(
                                              2,
                                            )}`}
                                          </Button>
                                        </div>

                                        <div className="wg-quantity">
                                          <span>
                                            <Button
                                              className="btn-quantity minus-btn"
                                              disabled={
                                                (quantities[
                                                  selectedSku?.sku_id
                                                ] || 0) === 0
                                              }
                                              onClick={() =>
                                                handleQuantityChange(
                                                  selectedSku?.sku_id,
                                                  "decrease",
                                                )
                                              }
                                            >
                                              -
                                            </Button>
                                          </span>
                                          <input
                                            type="text"
                                            name="number"
                                            value={
                                              selectedSku?.sku_id
                                                ? quantities[
                                                    selectedSku.sku_id
                                                  ] || 0
                                                : 0
                                            }
                                            readOnly
                                          />
                                          <Tooltip
                                            title={
                                              isDisabled
                                                ? "You have reached the bulk quantity limit"
                                                : selectedSku?.sku_status &&
                                                    selectedSku?.sku_status !==
                                                      "Visible"
                                                  ? "This item is not available right now"
                                                  : ""
                                            }
                                            placement="top"
                                            arrow
                                            open={isDisabled ? true : undefined}
                                          >
                                            <span>
                                              <Button
                                                className="btn-quantity plus-btn"
                                                onClick={() =>
                                                  handleQuantityChange(
                                                    selectedSku?.sku_id &&
                                                      selectedSku?.sku_id,
                                                    "increase",
                                                  )
                                                }
                                                disabled={
                                                  isDisabled ||
                                                  selectedSku?.sku_status !==
                                                    "Visible"
                                                }
                                              >
                                                +
                                              </Button>
                                            </span>
                                          </Tooltip>
                                        </div>
                                        {/* </> */}
                                        {/* )} */}
                                      </div>
                                    </div>
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                            </div>
                            {locationMessage && (
                              <p
                                style={{
                                  color: "#00b853",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                }}
                              >
                                {locationMessage}
                              </p>
                            )}
                          </div>
                        </div>
                        <div
                          className="nav-sw nav-prev-slider box-icon w_46"
                          style={{
                            position: "fixed",
                            left: "3px",
                            top: "60%",
                            transform: "translateY(-50%)",
                            zIndex: 1100,
                            visibility: "visible",
                            cursor: "pointer",
                            border: "none",
                          }}
                        >
                          <span className="icon icon-arrow-left"></span>
                        </div>
                        <div
                          className="nav-sw nav-next-slider box-icon w_46"
                          style={{
                            position: "fixed",
                            right: "3px",
                            top: "60%",
                            transform: "translateY(-50%)",
                            zIndex: 1100,
                            visibility: "visible",
                            cursor: "pointer",
                            border: "none",
                          }}
                        >
                          <span className="icon icon-arrow-right"></span>
                        </div>

                        <div className="tf-product-info-buy-button">
                          <form className="">
                            <div className="w-100">
                              {/* <Tooltip
                        title={
                          isEnable || cartData?.length === 0
                            ? "Add items to your cart to continue to checkout"
                            : ""
                        }
                        disableHoverListener={
                          !(isEnable || cartData?.length === 0)
                        }
                        placement="top"
                        arrow
                      > */}
                              <span
                                style={{
                                  width: "100%",
                                  display: "inline-block",
                                }}
                              >
                                <button
                                  style={{
                                    border: "none",
                                    width: "100%",
                                    backgroundColor:
                                      cartData?.length === 0 ||
                                      (quantities[selectedSku?.sku_id] || 0) < 1
                                        ? "#cccccc"
                                        : "",
                                    color:
                                      cartData?.length === 0 ||
                                      (quantities[selectedSku?.sku_id] || 0) < 1
                                        ? "#666666"
                                        : "",
                                    cursor:
                                      cartData?.length === 0 ||
                                      (quantities[selectedSku?.sku_id] || 0) < 1
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      cartData?.length === 0 ||
                                      (quantities[selectedSku?.sku_id] || 0) < 1
                                        ? "0.6"
                                        : "1",
                                  }}
                                  disabled={
                                    cartData?.length === 0 ||
                                    (quantities[selectedSku?.sku_id] || 0) < 1
                                  }
                                  onClick={() => {
                                    const accessToken = getDecryptedToken();
                                    if (!accessToken) {
                                      navigate("/checkout");
                                    } else {
                                      navigate("/checkout");
                                    }
                                  }}
                                  className="btns-full"
                                >
                                  Proceed to checkout
                                </button>
                              </span>
                              {/* </Tooltip> */}
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
